<template>
  <div class="col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end">
    <ul class="pagination pagination-modern mb-0 gap-1">
      <li class="page-item-modern" :class="{ disabled: isInFirstPage }">
        <button class="page-btn" :disabled="isInFirstPage" @click="onClickFirstPage" title="หน้าแรก">
          «
        </button>
      </li>
      <li class="page-item-modern" :class="{ disabled: isInFirstPage }">
        <button class="page-btn" :disabled="isInFirstPage" @click="onClickPreviousPage" title="ก่อนหน้า">
          ‹
        </button>
      </li>
      <li
        v-for="(page, i) in pages"
        :key="i"
        class="page-item-modern"
      >
        <button
          class="page-btn"
          :class="{ 'page-btn-active': isPageActive(page.name) }"
          @click="onClickPage(page.name)"
        >
          {{ page.name }}
        </button>
      </li>
      <li class="page-item-modern" :class="{ disabled: isInLastPage }">
        <button class="page-btn" :disabled="isInLastPage" @click="onClickNextPage" title="ถัดไป">
          ›
        </button>
      </li>
      <li class="page-item-modern" :class="{ disabled: isInLastPage }">
        <button class="page-btn" :disabled="isInLastPage" @click="onClickLastPage" title="หน้าสุดท้าย">
          »
        </button>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";

export default defineComponent({
  name: "table-pagination",
  props: {
    maxVisibleButtons: {
      type: Number,
      required: false,
      default: 5,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
    perPage: {
      type: Number,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
  },
  emits: ["page-change"],
  setup(props, { emit }) {
    const startPage = computed(() => {
      if (
        props.totalPages < props.maxVisibleButtons ||
        props.currentPage === 1 ||
        props.currentPage <= Math.floor(props.maxVisibleButtons / 2) ||
        (props.currentPage + 2 > props.totalPages &&
          props.totalPages === props.maxVisibleButtons)
      ) {
        return 1;
      }

      if (props.currentPage + 2 > props.totalPages) {
        return props.totalPages - props.maxVisibleButtons + 1;
      }

      return props.currentPage - 2;
    });

    const endPage = computed(() => {
      return Math.min(
        startPage.value + props.maxVisibleButtons - 1,
        props.totalPages
      );
    });

    const pages = computed(() => {
      const range: Array<{
        name: number;
        isDisabled: boolean;
      }> = [];

      for (let i = startPage.value; i <= endPage.value; i += 1) {
        range.push({
          name: i,
          isDisabled: i === props.currentPage,
        });
      }

      return range;
    });

    const isInFirstPage = computed(() => {
      return props.currentPage === 1;
    });
    const isInLastPage = computed(() => {
      return props.currentPage === props.totalPages;
    });

    const onClickFirstPage = () => {
      emit("page-change", 1);
    };
    const onClickPreviousPage = () => {
      emit("page-change", props.currentPage - 1);
    };
    const onClickPage = (page: number) => {
      emit("page-change", page);
    };
    const onClickNextPage = () => {
      emit("page-change", props.currentPage + 1);
    };
    const onClickLastPage = () => {
      emit("page-change", props.totalPages);
    };
    const isPageActive = (page: number) => {
      return props.currentPage === page;
    };

    return {
      startPage,
      endPage,
      pages,
      isInFirstPage,
      isInLastPage,
      onClickFirstPage,
      onClickPreviousPage,
      onClickPage,
      onClickNextPage,
      onClickLastPage,
      isPageActive,
    };
  },
});
</script>

<style scoped>
.pagination-modern { display: flex; list-style: none; padding: 0; margin: 0; }
.page-item-modern { display: inline-flex; }
.page-item-modern.disabled .page-btn { opacity: 0.35; cursor: not-allowed; }
.page-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #374151;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.page-btn:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #323563;
}
.page-btn-active {
  background: #323563 !important;
  border-color: #323563 !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(50,53,99,0.3);
}
</style>
